// src/app/(dashboard)/meter/components/MeterListClient.tsx
"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import type { MeterListResponse, MeterRow } from "../types";
import { MeterCard } from "./MeterCard";
import { MeterSearchForm } from "./MeterSearchForm";
import { MeterPagination } from "./MeterPagination";
import { RefreshStatusBadge } from "./RefreshStatusBadge";
import { useIntervalRefresh } from "@/hooks/useIntervalRefresh";

interface MeterListClientProps {
  initialData: MeterListResponse;
  page: number;
  pageSize: number;
  q: string;
  status: string;
  meterType: string;
}

export function MeterListClient({
  initialData,
  page,
  pageSize,
  q,
  status,
  meterType,
}: MeterListClientProps) {
  const [data, setData] = useState<MeterListResponse>(initialData);
  const [isStale, setIsStale] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const router = useRouter();
  const pathname = usePathname();
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  const [now, setNow] = useState<number>(0);

  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));

  const handleRefresh = useCallback(async () => {
    const qs = new URLSearchParams();
    qs.set("page", String(page));
    qs.set("pageSize", String(pageSize));
    if (q) qs.set("q", q);
    if (status) qs.set("status", status);
    if (meterType) qs.set("meterType", meterType);
    qs.set("_t", String(Date.now()));

    const res = await fetch(`/api/meter?${qs.toString()}`, {
      cache: "no-store",
    });

    if (!res.ok) return;

    const fresh: MeterListResponse = await res.json();
    setData(fresh);
    setNow(Date.now());
    setIsStale(false); // Clear stale flag when fresh data arrives
  }, [page, pageSize, q, status, meterType]);

  const {
    secondsUntilRefresh,
    isRefreshing,
    isOnline,
    triggerManualRefresh,
  } = useIntervalRefresh({
    offsetMinutes: 3.5,
    onRefresh: handleRefresh,
  });

  const refreshBadge = (
    <RefreshStatusBadge
      secondsUntilRefresh={secondsUntilRefresh}
      isRefreshing={isRefreshing}
      isOnline={isOnline}
      onManualRefresh={triggerManualRefresh}
    />
  );

  const searchForm = (
    <MeterSearchForm
      q={q}
      pageSize={pageSize}
      status={status}
      meterType={meterType}
    />
  );

  const pagination = (
    <MeterPagination
      page={page}
      totalPages={totalPages}
      q={q}
      pageSize={pageSize}
      status={status}
      meterType={meterType}
    />
  );

  /* ---- auto-fit page size to the viewport, so a full page never needs scrolling ---- */

  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;
  const qRef = useRef(q);
  qRef.current = q;
  const statusRef = useRef(status);
  statusRef.current = status;
  const meterTypeRef = useRef(meterType);
  meterTypeRef.current = meterType;

  const recalcPageSize = useCallback(() => {
    const grid = gridRef.current;
    if (!grid || data.items.length === 0) return;
    // Guard against a transient 0-sized (or otherwise implausible) viewport
    // read during navigation/resize — never a real state to size a page for.
    if (window.innerWidth < 200 || window.innerHeight < 200) return;

    const w = window.innerWidth;
    const cols = w >= 1280 ? 4 : w >= 1024 ? 3 : w >= 640 ? 2 : 1;
    const rows = Math.max(1, Math.ceil(data.items.length / cols));
    const rowHeight = grid.scrollHeight / rows;
    if (!Number.isFinite(rowHeight) || rowHeight <= 0) return;

    const gap = 12; // matches the grid's gap-3
    const top = grid.getBoundingClientRect().top;
    const available = window.innerHeight - top - 8;
    const fitRows = Math.max(1, Math.floor((available + gap) / (rowHeight + gap)));
    const idealPageSize = fitRows * cols;

    if (idealPageSize > 0 && idealPageSize !== pageSizeRef.current) {
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("pageSize", String(idealPageSize));
      if (qRef.current) params.set("q", qRef.current);
      if (statusRef.current) params.set("status", statusRef.current);
      if (meterTypeRef.current) params.set("meterType", meterTypeRef.current);
      router.replace(`${pathname}?${params.toString()}`);
    }
  }, [data.items.length, pathname, router]);

  // Re-measure whenever the grid's actual rendered size changes — mount,
  // window resize, or a late reflow (e.g. web fonts swapping in after the
  // first paint). A one-shot measurement on mount can be slightly stale;
  // ResizeObserver keeps re-checking until the real, settled size is seen.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;

    let debounceTimer: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(recalcPageSize, 150);
    });
    observer.observe(grid);

    return () => {
      clearTimeout(debounceTimer);
      observer.disconnect();
    };
  }, [recalcPageSize]);

  const lastVisibilityRefreshRef = useRef<number>(0);

  // Refresh on visibility change (tab switch, window focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      setNow(Date.now());

      const timeSinceLastRefresh =
        Date.now() - lastVisibilityRefreshRef.current;
      if (timeSinceLastRefresh < 5000) return;

      lastVisibilityRefreshRef.current = Date.now();
      triggerManualRefresh();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [triggerManualRefresh]);

  // Refresh on mount (handles back button, forward button, direct navigation)
  useEffect(() => {
    setIsStale(true); // Mark as stale immediately
    handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="sticky top-14 z-20 bg-slate-50 pt-4 dark:bg-slate-950">
        <div
          className={[
            "flex items-center justify-between gap-3",
            "rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm",
            "dark:border-slate-800 dark:bg-slate-900",
          ].join(" ")}
        >
          <div>
            <h1 className="m-0 text-[22px] font-bold text-slate-900 dark:text-slate-50">
              PEA Meters
            </h1>
            <div className="mt-1 flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
              <span>
                จำนวนมิเตอร์ทั้งหมด:{" "}
                <b className="text-slate-900 dark:text-slate-50">
                  {data.total}
                </b>
              </span>
              <span className="hidden sm:inline-flex">{refreshBadge}</span>
            </div>
          </div>

          <div className="hidden sm:block">{searchForm}</div>

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? "แสดงน้อยลง" : "แสดงตัวกรองเพิ่มเติม"}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 sm:hidden"
          >
            <ChevronDownIcon
              className={expanded ? "rotate-180 transition-transform" : "transition-transform"}
            />
          </button>
        </div>

        {expanded && (
          <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:hidden">
            {refreshBadge}
            {pagination}
            {searchForm}
          </div>
        )}
      </div>

      <div className="mt-4 hidden justify-center sm:flex">{pagination}</div>

      <div
        ref={gridRef}
        className={[
          "mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
          "transition-opacity duration-200",
          isRefreshing || isStale ? "opacity-50" : "opacity-100",
        ].join(" ")}
      >
        {now > 0 &&
          data.items.map((meter: MeterRow) => (
            <MeterCard key={meter.id} meter={meter} now={now} />
          ))}

        {data.items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
            ไม่พบข้อมูล
          </div>
        )}
      </div>
    </>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
